import { useCallback, useEffect, useRef, useState } from 'react';

import { stopMediaStream } from '@/entities/recording';

import { getCameraConstraints } from '../lib/recorderMedia';
import { useMountedRef } from './useMountedRef';

/**
 * The camera and microphone the user picks before recording, and the stream shown in the camera bubble: a live
 * camera preview during setup (while the camera is on), then whatever the recording shows via showPreview().
 */
export const useRecorderDevices = (isSetup: boolean) => {
  const [cameraEnabled, setCameraEnabled] = useState(true);
  const [microphoneEnabled, setMicrophoneEnabled] = useState(true);
  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);
  const [microphones, setMicrophones] = useState<MediaDeviceInfo[]>([]);
  const [selectedCameraId, setSelectedCameraId] = useState('');
  const [selectedMicrophoneId, setSelectedMicrophoneId] = useState('');
  const [previewStream, setPreviewStream] = useState<MediaStream | null>(null);
  const setupPreviewRef = useRef<MediaStream | null>(null);
  const mountedRef = useMountedRef();

  // Device labels are only exposed once a permission is granted, so the lists are refreshed after each request.
  const refreshDevices = useCallback(async () => {
    if (!navigator.mediaDevices?.enumerateDevices) return;
    const devices = await navigator.mediaDevices.enumerateDevices();
    const nextCameras = devices.filter((device) => device.kind === 'videoinput');
    const nextMicrophones = devices.filter((device) => device.kind === 'audioinput');

    if (!mountedRef.current) return;
    setCameras(nextCameras);
    setMicrophones(nextMicrophones);
    setSelectedCameraId((current) => current || nextCameras[0]?.deviceId || '');
    setSelectedMicrophoneId((current) => current || nextMicrophones[0]?.deviceId || '');
  }, [mountedRef]);

  const stopSetupPreview = useCallback(() => {
    stopMediaStream(setupPreviewRef.current);
    setupPreviewRef.current = null;
  }, []);

  /** Stops the setup preview and empties the camera bubble. */
  const clearPreview = useCallback(() => {
    stopSetupPreview();
    if (mountedRef.current) setPreviewStream(null);
  }, [mountedRef, stopSetupPreview]);

  /** Shows a stream the caller owns (and stops) in the camera bubble. */
  const showPreview = useCallback(
    (stream: MediaStream | null) => {
      if (mountedRef.current) setPreviewStream(stream);
    },
    [mountedRef],
  );

  useEffect(() => {
    if (!isSetup) return;

    if (!cameraEnabled || !navigator.mediaDevices?.getUserMedia) {
      clearPreview();
      void refreshDevices();
      return;
    }

    let cancelled = false;

    const startPreview = async () => {
      // Only the stream stops: the camera bubble keeps it until the new camera is ready, instead of flashing empty.
      stopSetupPreview();

      try {
        const stream = await navigator.mediaDevices.getUserMedia(getCameraConstraints(selectedCameraId));
        if (cancelled) {
          stopMediaStream(stream);
          return;
        }
        setupPreviewRef.current = stream;
        setPreviewStream(stream);
        await refreshDevices();
      } catch {
        if (!cancelled) {
          setCameraEnabled(false);
          await refreshDevices();
        }
      }
    };

    void startPreview();

    return () => {
      cancelled = true;
    };
  }, [cameraEnabled, clearPreview, isSetup, refreshDevices, selectedCameraId, stopSetupPreview]);

  return {
    cameraEnabled,
    cameras,
    clearPreview,
    microphoneEnabled,
    microphones,
    previewStream,
    selectedCameraId,
    selectedMicrophoneId,
    showPreview,
    setCameraEnabled,
    setMicrophoneEnabled,
    setSelectedCameraId,
    setSelectedMicrophoneId,
  };
};
